import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import StartupCard from "../components/StartupCard";
import { ArrowUpRight, Award, Filter, Bell } from "lucide-react";

// 6-second brand hype video — royalty-free fashion model loop, autoplay muted for logged-out visitors
const HERO_VIDEO = "https://cdn.coverr.co/videos/coverr-a-fashion-model/1080p.mp4";
const HERO_IMAGE = "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=2000&q=85&auto=format&fit=crop";

export default function Home() {
    const { user, ready } = useAuth();
    const [startups, setStartups] = useState([]);
    const [drops, setDrops] = useState([]);
    const showVideo = ready && !user;

    useEffect(() => {
        api.get("/startups", { params: { sort: "featured" } }).then((r) => setStartups(r.data)).catch(() => {});
        api.get("/drops").then((r) => setDrops(r.data)).catch(() => {});
    }, []);

    return (
        <main className="relative">
            {/* HERO — magazine cover, editorial, asymmetric */}
            <section className="relative overflow-hidden min-h-[92vh] md:min-h-screen flex flex-col justify-between text-white">
                {/* Background image / video */}
                <div className="absolute inset-0 z-0">
                    {showVideo ? (
                        <video
                            data-testid="hero-video"
                            src={HERO_VIDEO}
                            autoPlay muted loop playsInline
                            poster={HERO_IMAGE}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <img
                            src={HERO_IMAGE}
                            srcSet={`${HERO_IMAGE.replace('w=2000', 'w=800')} 800w, ${HERO_IMAGE.replace('w=2000', 'w=1200')} 1200w, ${HERO_IMAGE} 2000w`}
                            sizes="100vw"
                            alt=""
                            loading="eager"
                            fetchpriority="high"
                            className="w-full h-full object-cover"
                        />
                    )}
                    {/* Cinematic vignette + heavy left-side wash so the title reads */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_60%,transparent_0%,rgba(0,0,0,0.55)_75%)]" />
                    {/* Subtle grain */}
                    <div className="absolute inset-0 opacity-[0.14] mix-blend-overlay"
                        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n3'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n3)'/></svg>\")" }} />
                </div>

                {/* Top masthead strip */}
                <div className="relative z-10 max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-12 pt-8 md:pt-12">
                    <div className="flex items-center justify-between font-mono-tag text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/70">
                        <span data-testid="hero-eyebrow">Issue N° 001 · Vol I · MMXXVI</span>
                        <span className="hidden sm:inline">India · Editorial</span>
                    </div>
                    <div className="mt-3 md:mt-4 h-px w-full bg-white/20" />
                </div>

                {/* Main title block — left-aligned, editorial */}
                <div className="relative z-10 max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-12 grid grid-cols-12 gap-4 items-end pb-16 md:pb-24">
                    {/* Left column — title */}
                    <div className="col-span-12 md:col-span-9">
                        <p className="font-serif-editorial italic text-white/70 text-sm md:text-base mb-4 md:mb-6">
                            — The definitive guide to India's fashion underground.
                        </p>
                        <h1
                            data-testid="landing-title"
                            className="font-serif-editorial text-white leading-[0.82] tracking-[-0.03em] text-[22vw] sm:text-[18vw] md:text-[15vw] lg:text-[13vw] xl:text-[220px]"
                            style={{ fontWeight: 900 }}
                        >
                            Primates
                        </h1>
                        <div className="mt-6 md:mt-8 flex items-center gap-4">
                            <span className="h-px flex-1 max-w-[80px] bg-brand-saffron" />
                            <p className="font-mono-tag text-[10px] md:text-xs uppercase tracking-[0.4em] text-brand-saffron">
                                Aesthetic · Iconic · Underdogs
                            </p>
                        </div>
                    </div>

                    {/* Right column — rotated caption on md+, stacked below on mobile */}
                    <div className="hidden md:flex md:col-span-3 justify-end">
                        <div className="rotate-180 [writing-mode:vertical-rl] font-mono-tag text-[10px] uppercase tracking-[0.4em] text-white/60 whitespace-nowrap border-l border-white/20 pl-3">
                            From the world · For India · Curated · Editor-picked
                        </div>
                    </div>
                </div>

                {/* Bottom strip — CTAs + editorial stats */}
                <div className="relative z-10 max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-12 pb-8 md:pb-10">
                    <div className="h-px w-full bg-white/20 mb-6 md:mb-8" />
                    <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                            <Link to="/discover">
                                <Button data-testid="landing-cta-discover" size="lg"
                                    className="w-full sm:w-auto bg-white text-black hover:bg-white/90 font-semibold px-8 md:px-10 py-5 md:py-6 text-base rounded-none tracking-wide">
                                    Enter the feed <ArrowUpRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/drops">
                                <Button data-testid="landing-cta-drops" size="lg" variant="outline"
                                    className="w-full sm:w-auto border-white/40 text-white bg-transparent hover:bg-white/10 font-semibold px-8 md:px-10 py-5 md:py-6 text-base rounded-none tracking-wide">
                                    See the drops
                                </Button>
                            </Link>
                        </div>
                        {/* Editorial stats */}
                        <div className="grid grid-cols-3 gap-4 md:gap-8 md:ml-auto text-white">
                            <Stat n={startups.length || "17"} label="Brands" />
                            <Stat n="15" label="Cities" />
                            <Stat n="7" label="Categories" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Marquee tagline */}
            <section className="border-y border-white/10 bg-brand-saffron py-4 overflow-hidden">
                <div className="whitespace-nowrap animate-[marquee_40s_linear_infinite] font-display font-extrabold text-2xl md:text-4xl uppercase tracking-tighter text-black">
                    {Array(4).fill(0).map((_, i) => (
                        <span key={i} className="mx-8">Not every brand can list here ✦ We sort the styles for you ✦ From the world, for India ✦</span>
                    ))}
                </div>
            </section>

            {/* 3-panel entry */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="rangoli-divider mb-14 text-brand-saffron/50">✦ EXPLORE ✦</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                        { to: "/", label: "HOME", body: "The heritage. The mission. The manifesto.", testid: "panel-home" },
                        { to: "/discover", label: "DISCOVER", body: "Browse hand-picked Indian fashion brands.", testid: "panel-discover" },
                        { to: "/drops", label: "DROPS", body: "New collection campaigns from listed brands.", testid: "panel-drops" },
                    ].map((p) => (
                        <Link
                            key={p.label}
                            to={p.to}
                            data-testid={p.testid}
                            className="group relative overflow-hidden grain-card rounded-md p-8 md:p-10 hover:border-brand-saffron transition-colors min-h-[240px] flex flex-col justify-between"
                        >
                            <div>
                                <h3 className="font-display font-extrabold text-4xl md:text-5xl uppercase tracking-tighter text-white group-hover:text-brand-saffron transition-colors leading-none">
                                    {p.label}
                                </h3>
                                <p className="mt-4 font-serif-editorial italic text-sm text-white/60 max-w-xs">
                                    {p.body}
                                </p>
                            </div>
                            <span className="mt-8 inline-flex items-center gap-1 font-mono-tag text-[10px] uppercase tracking-widest text-brand-saffron group-hover:text-brand-marigold transition-colors">
                                ENTER →
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Manifesto */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20 border-t border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { icon: Award, title: "Editor-picked", body: "Not every brand can list here. Every application is reviewed — so the feed stays worth scrolling." },
                        { icon: Filter, title: "We sort for you", body: "By vibe, city, category, craft. India is huge. Discovery shouldn't feel like homework." },
                        { icon: Bell, title: "Follow the pack", body: "Hit the heart on brands you love. Get pinged when they drop something new." },
                    ].map((f, i) => (
                        <div key={i} className="grain-card p-8 rounded-md">
                            <f.icon className="w-8 h-8 text-brand-saffron mb-4" />
                            <h3 className="font-display font-bold text-2xl mb-2 tracking-tight">{f.title}</h3>
                            <p className="text-white/60 text-sm">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured startups */}
            {startups.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24 border-t border-white/10">
                    <div className="flex items-end justify-between gap-4 mb-10">
                        <div>
                            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ on the roster</p>
                            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter uppercase">Editor's <span className="text-brand-saffron">picks</span></h2>
                        </div>
                        <Link to="/discover" className="font-mono-tag text-xs uppercase text-white/70 hover:text-brand-saffron transition-colors flex items-center gap-1">
                            See all <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[240px]">
                        {startups.slice(0, 6).map((s, i) => (
                            <StartupCard key={s.id} startup={s} variant={i === 0 || i === 3 ? "tall" : "default"} />
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}

function OrnamentalFrame() {
    return null;
}

function Stat({ n, label }) {
    return (
        <div>
            <p className="font-serif-editorial text-3xl md:text-5xl leading-none" style={{ fontWeight: 900 }}>
                {n}
            </p>
            <p className="mt-1 font-mono-tag text-[10px] uppercase tracking-[0.25em] text-white/60">{label}</p>
        </div>
    );
}

